import './style/portalSelect.scss';
import {AwesomeIcon, ErrorType, remToPx} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {
    CSSProperties,
    Fragment,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef
} from 'react';
import {createPortal} from 'react-dom';
import {usePortalDismiss} from '../usePortalDismiss';
import {useTranslation} from 'react-i18next';
import {
    portalItemIsGroup,
    PortalSelectGroup,
    PortalSelectItem,
    PortalSelectOption,
    PortalSelectValue
} from '../../../util/types';

interface Props<VT = string> {
    onChange: (value: VT) => MouseEventHandler;
    onToggleOpen: MouseEventHandler;
    onClose: VoidFunction;
    onSelectAll: MouseEventHandler;
    onDeselectAll: MouseEventHandler;
    parseSelectValue?: (value: PortalSelectValue<VT>) => string;
    open: boolean;
    items: Array<PortalSelectItem<VT>>;
    value: PortalSelectValue<VT>;
    disabled: boolean;
    label?: ReactNode;
    error?: ErrorType;
    className?: string;
    itemsClassName?: string;
    placeholder?: string;
    selectLabel?: string;
    portalTarget?: Element | string;
    itemsPosition: 'top' | 'bottom';
    itemsHeight?: number | string;
    shape: 'square' | 'rounded';
    size: 'small' | 'medium' | 'large';
    controls?: Array<'selectAll' | 'deselectAll'>;
    multiple: boolean;
    disableAutoScroll: boolean;
    outlined: boolean;
    /** Renders the trigger without its box shadow (e.g. inside dialogs). */
    noShadow: boolean;
    /** Unified form typography: 0.9rem/500 label, 1rem/500 value. */
    uniformFont: boolean;
}

export default function PortalSelect<VT = string>(props: Props<VT>) {
    const {t} = useTranslation();
    const {
        onChange,
        onClose,
        open,
        items,
        value,
        disabled,
        className,
        itemsClassName,
        placeholder,
        label,
        error,
        onToggleOpen,
        itemsPosition,
        itemsHeight,
        shape,
        size,
        onSelectAll,
        onDeselectAll,
        controls,
        multiple,
        disableAutoScroll,
        outlined,
        noShadow,
        uniformFont,
        parseSelectValue
    } = props;

    // Not using memo to still allow finding the element if it has not rendered immediately
    let portalTarget: Element;
    if (props.portalTarget === undefined) portalTarget = document.body;
    else if (typeof props.portalTarget === 'string')
        portalTarget = document.querySelector(props.portalTarget) ?? document.body;
    else portalTarget = props.portalTarget;

    const [hasGroupItems, options] = useMemo(() => {
        const hasGroupItems = items.some((item: PortalSelectItem<VT>) => portalItemIsGroup(item));
        let options: Array<PortalSelectOption<VT>> = [];
        for (const item of items) {
            if (portalItemIsGroup(item)) options = options.concat((item as PortalSelectGroup<VT>).items);
            else options.push(item as PortalSelectOption<VT>);
        }
        return [hasGroupItems, options];
    }, [items]);
    const activeValue = useMemo(() => {
        if (Array.isArray(value))
            return options.filter((option: PortalSelectOption<VT>) => value.includes(option.value));
        return options.find((option: PortalSelectOption<VT>) => option.value === value);
    }, [value, options]);

    const rootRef = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<HTMLDivElement>(null);
    const hoveringItems = usePortalDismiss({
        open,
        onClose,
        onReposition: setItemsPosition,
        insideRefs: [rootRef, itemsRef],
        itemsRef
    });

    const itemIsSelected = useCallback(
        (item: PortalSelectOption<VT>) => {
            if (Array.isArray(value)) return value.includes(item.value);
            return value === item.value;
        },
        [value]
    );

    const [selectValue, isPlaceholder] = useMemo(() => {
        if (Array.isArray(activeValue)) {
            const activeSelectable = activeValue.filter((item: PortalSelectOption<VT>) => item.selectable !== false);
            if (parseSelectValue !== undefined)
                return [parseSelectValue(activeSelectable.map(option => option.value)), false];
            if (props.selectLabel)
                return [props.selectLabel.replace('{count}', activeSelectable.length.toString()), false];
            if (!activeValue.length) return [placeholder ?? '', true];
            if (activeValue.length === 1) return [activeValue[0].selectedLabel ?? activeValue[0].label, false];

            if (activeSelectable.length === 1)
                return [activeSelectable[0].selectedLabel ?? activeSelectable[0].label, false];

            return [
                t('controls.portalSelect.selectedCount', '{{count}} geselecteerd', {count: activeSelectable.length}),
                false
            ];
        } else if (parseSelectValue !== undefined && activeValue) return [parseSelectValue(activeValue.value), false];
        else if (props.selectLabel) return [props.selectLabel, false];
        if (activeValue) return [activeValue.selectedLabel ?? activeValue.label, false];
        return [placeholder ?? '', true];
    }, [value, placeholder, activeValue, props.selectLabel, t]);

    function setItemsPosition() {
        requestAnimationFrame(() => {
            const select = selectRef.current;
            const items = itemsRef.current;
            if (!select || !items) return;
            const margin = remToPx(0.5);
            const gap = remToPx(0.25);
            const viewportWidth = document.documentElement.clientWidth;
            const viewportHeight = document.documentElement.clientHeight;
            const selectRect = select.getBoundingClientRect();

            const propMaxHeight = typeof itemsHeight === 'number' ? itemsHeight + 'px' : itemsHeight;

            // Measure the list under its natural constraints only (viewport
            // width cap + the itemsHeight prop). Every constraint is rewritten
            // on every run — the position is recomputed live (window resize,
            // ResizeObserver) and a stale min-width would beat a fresh
            // max-width. left is neutralized before measuring: shrink-to-fit
            // width must not be capped by the previous run's offset.
            items.style.setProperty('left', margin + 'px');
            items.style.setProperty('max-width', viewportWidth - 2 * margin + 'px');
            items.style.setProperty('min-width', Math.min(selectRect.width, viewportWidth - 2 * margin) + 'px');
            items.style.setProperty('max-height', propMaxHeight ?? 'none');
            const naturalRect = items.getBoundingClientRect();

            // Open on the preferred side, but flip when the list — at the
            // size it would actually render, so an itemsHeight-capped list
            // that scrolls internally doesn't count as cut off — doesn't fit
            // there and the other side has more room.
            const spaceBelow = viewportHeight - selectRect.bottom - gap - margin;
            const spaceAbove = selectRect.top - gap - margin;
            let position = itemsPosition;
            if (position === 'bottom' && naturalRect.height > spaceBelow && spaceAbove > spaceBelow) position = 'top';
            else if (position === 'top' && naturalRect.height > spaceAbove && spaceBelow > spaceAbove)
                position = 'bottom';
            let space = Math.max(position === 'bottom' ? spaceBelow : spaceAbove, 0);
            // Neither side usable (a trigger wedged between a fixed header and a
            // fixed bottom bar on a short/narrow window) would leave the list at
            // max-height 0 — it opens, but nothing is on screen and the control
            // reads as dead. Fall back to the whole viewport band and let the
            // list scroll inside it.
            const minimumSpace = remToPx(8);
            const useViewportBand = space < minimumSpace;
            if (useViewportBand) space = Math.max(viewportHeight - 2 * margin, 0);
            // The viewport cap only ever shrinks the itemsHeight prop, never
            // stretches it.
            items.style.setProperty('max-height', propMaxHeight ? `min(${propMaxHeight}, ${space}px)` : space + 'px');

            const itemsRect = items.getBoundingClientRect();
            let top: number;
            if (useViewportBand) top = margin;
            else if (position === 'bottom') top = selectRect.bottom + gap;
            else top = selectRect.top - gap - itemsRect.height;
            top = Math.max(Math.min(top, viewportHeight - margin - itemsRect.height), margin);
            const left = Math.max(Math.min(selectRect.left, viewportWidth - margin - itemsRect.width), margin);
            items.style.setProperty('top', top + 'px');
            items.style.setProperty('left', left + 'px');
        });
    }

    function scrollToSelected() {
        if (!activeValue || disableAutoScroll) return;
        const item = itemsRef.current?.querySelector(`.portal-select-item.selected`);
        if (!item) return;
        item.scrollIntoView({block: 'start'});
    }

    function renderLabel() {
        if (!label) return null;
        return (
            <label className='portal-select-label' onClick={onToggleOpen}>
                {label}
            </label>
        );
    }

    function renderSelect() {
        return (
            <div
                className={classNames(
                    'portal-select-container',
                    shape,
                    `size-${size}`,
                    outlined && 'outlined',
                    noShadow && 'no-shadow'
                )}
                onClick={onToggleOpen}
                ref={selectRef}
            >
                <label className={classNames('portal-select-value', isPlaceholder && 'placeholder')}>
                    {selectValue}
                </label>
                <AwesomeIcon name='chevron-down' className={classNames('portal-chev-icon', open && 'open')} />
            </div>
        );
    }

    function renderError() {
        if (!error) return null;
        return <label className='portal-select-error'>{error}</label>;
    }

    function renderItems() {
        if (!open) return null;
        let content: ReactNode;
        if (hasGroupItems) content = renderItemGroups();
        else content = renderSingleItems();
        return createPortal(content, portalTarget);
    }

    function renderItemGroups() {
        const style: CSSProperties = {};
        if (typeof itemsHeight === 'string') style.maxHeight = itemsHeight;
        else if (typeof itemsHeight === 'number') style.maxHeight = itemsHeight + 'px';
        const hasControls = !!controls?.length;
        return (
            <div
                className={classNames(
                    'portal-select-items groups',
                    open && 'open',
                    !hasControls && 'no-controls',
                    shape,
                    `size-${size}`,
                    itemsClassName
                )}
                ref={itemsRef}
                style={style}
                onMouseEnter={() => {
                    hoveringItems.current = true;
                }}
                onMouseLeave={() => {
                    hoveringItems.current = false;
                }}
            >
                {renderControls()}
                {items.map(renderItemGroup)}
            </div>
        );
    }

    function renderSingleItems() {
        const style: CSSProperties = {};
        if (typeof itemsHeight === 'string') style.maxHeight = itemsHeight;
        else if (typeof itemsHeight === 'number') style.maxHeight = itemsHeight + 'px';
        const hasControls = !!controls?.length;
        return (
            <div
                className={classNames(
                    'portal-select-items',
                    open && 'open',
                    !hasControls && 'no-controls',
                    shape,
                    itemsClassName,
                    `size-${size}`
                )}
                ref={itemsRef}
                style={style}
                onMouseEnter={() => {
                    hoveringItems.current = true;
                }}
                onMouseLeave={() => {
                    hoveringItems.current = false;
                }}
            >
                {renderControls()}
                {items.map(renderItem)}
            </div>
        );
    }

    function renderControls() {
        if (!multiple || !controls?.length) return null;
        return (
            <div className='portal-select-item-controls'>
                {controls.includes('selectAll') && (
                    <div className='portal-select-item-control' onClick={onSelectAll}>
                        <label>{t('controls.portalSelect.selectAll', 'Alles')}</label>
                    </div>
                )}
                {controls.includes('deselectAll') && (
                    <div className='portal-select-item-control' onClick={onDeselectAll}>
                        <label>{t('controls.portalSelect.clear', 'Wis')}</label>
                    </div>
                )}
            </div>
        );
    }

    function renderItemGroup(group: PortalSelectItem<VT>, index: number) {
        if (!portalItemIsGroup(group)) return renderItem(group, index);
        return (
            <Fragment key={index}>
                <label className='portal-select-group-label'>{group.groupLabel}</label>
                {group.items.map(renderItem)}
            </Fragment>
        );
    }

    function renderItem(item: PortalSelectOption<VT>, index: number) {
        if (item.selectable === false) return null;
        const isSelected = itemIsSelected(item);
        const isDisabled = item.disabled === true;
        return (
            <div
                key={index}
                className={classNames('portal-select-item', isSelected && 'selected', isDisabled && 'disabled', {
                    single: !multiple
                })}
                onClick={isDisabled ? undefined : onChange(item.value)}
            >
                {multiple && renderCheck(isSelected)}
                {renderSelectableItem(item)}
                {!multiple && renderCheckIcon(isSelected)}
            </div>
        );
    }

    function renderCheck(isSelected: boolean) {
        return (
            <div className={classNames('portal-select-check', isSelected && 'selected')}>
                <AwesomeIcon name='check' className='portal-check-icon' />
            </div>
        );
    }

    function renderCheckIcon(isSelected: boolean) {
        return (
            <AwesomeIcon name='check' className={classNames('portal-check-icon', 'single', {selected: isSelected})} />
        );
    }

    function renderSelectableItem(item: PortalSelectOption<VT>) {
        const {label} = item;
        if (React.isValidElement(label)) return label;
        return <label className='portal-select-item-label'>{label}</label>;
    }

    useEffect(() => {
        let observer: ResizeObserver | undefined;
        if (open) {
            scrollToSelected();
            // The list can grow after opening (async items, e.g. the device
            // list) — reposition so it stays within the viewport.
            if (itemsRef.current && typeof ResizeObserver !== 'undefined') {
                observer = new ResizeObserver(() => setItemsPosition());
                observer.observe(itemsRef.current);
            }
        }
        return () => {
            observer?.disconnect();
        };
    }, [open]);

    return (
        <div
            className={classNames('portal-select', disabled && 'disabled', uniformFont && 'uniform-font', className)}
            ref={rootRef}
        >
            {renderLabel()}
            {renderSelect()}
            {renderError()}
            {renderItems()}
        </div>
    );
}

PortalSelect.displayName = 'PortalSelect';
